import React, { useRef } from 'react'
import styled from '@emotion/styled'
import { Global, css } from '@emotion/react'
import { Filters } from '@/components/templates/filters/Filters'
import { IEstructuraFiltros, FiltersControl } from '@/components/templates/filters/Filters.interface'
import { FiltroPreciosCustom } from './FiltroPreciosCustom'

interface FiltersDrawerProps extends IEstructuraFiltros {
	open: boolean;
	onClose: () => void;
}

export const FiltersDrawer = ({ open, onClose, filtros, onFiltersReady, ...filtersProps }: FiltersDrawerProps) => {
	const filtersControlRef = useRef<FiltersControl | null>(null)

	const handleFiltersReady = (control: FiltersControl) => {
		filtersControlRef.current = control
		onFiltersReady?.(control)
	}

	const handlePriceFilterChange: Parameters<typeof FiltroPreciosCustom>[0]['onFilterChange'] = (filterId, newFilter) => {
		filtersControlRef.current?.updateFilter(filterId, newFilter)
	}

	const preciosFilterDef = filtros.find(f => f.id === 'precios')

	return (
		<>
			{/*
			  Los dropdowns de filtros (MUI Select) se portan a document.body con
			  z-index: 1300 por defecto, menor al del panel del drawer (2200), por lo
			  que quedaban ocultos detrás mientras el drawer estaba abierto.
			*/}
			<Global
				styles={css`
					.MuiPopover-root {
						z-index: 2300 !important;
					}
				`}
			/>

			{/* Overlay */}
			<Overlay visible={open} onClick={onClose} aria-hidden="true" />

			{/* Drawer panel */}
			<DrawerPanel open={open} role="dialog" aria-modal="true" aria-label="Filtros">
				<DrawerHeader>
					<DrawerTitle>Filtros</DrawerTitle>
					<CloseButton type="button" onClick={onClose} aria-label="Cerrar filtros">
						✕
					</CloseButton>
				</DrawerHeader>

				<DrawerBody>
					<Filters
						filtros={filtros}
						onFiltersReady={handleFiltersReady}
						customBlockFilterIds={['precios']}
						customBlock={
							preciosFilterDef
								? <FiltroPreciosCustom
									filtroDefinicion={preciosFilterDef}
									onFilterChange={handlePriceFilterChange}
								/>
								: undefined
						}
						{...filtersProps}
						isMobile={false}
					/>
				</DrawerBody>
			</DrawerPanel>
		</>
	)
}

/* ─── Styled Components ─────────────────────────────────────── */

const Overlay = styled.div<{ visible: boolean }>`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	z-index: 2100;
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
	transition: opacity 0.25s ease;
`

const DrawerPanel = styled.div<{ open: boolean }>`
	position: fixed;
	right: 0;
	top: 0;
	height: 100%;
	width: 380px;
	max-width: 100vw;
	background: #fff;
	z-index: 2200;
	display: flex;
	flex-direction: column;
	transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
	transition: transform 0.3s ease;
	box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);

	@media (max-width: 480px) {
		width: 100vw;
	}
`

const DrawerHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	border-bottom: 1px solid #E0E0E0;
	flex-shrink: 0;
`

const DrawerTitle = styled.h2`
	margin: 0;
	font-family: nunito, sans-serif;
	font-size: 18px;
	font-weight: 700;
	color: #333;
`

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 20px;
	color: #666;
	cursor: pointer;
	padding: 4px 8px;
	line-height: 1;

	&:hover {
		color: #333;
	}
`

const DrawerBody = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 16px;

	/* Override ContenedorFiltros from design system */
	> div:first-of-type {
		background-color: transparent !important;
		max-width: 100% !important;
		padding: 0 !important;
		border-radius: 0 !important;
	}

	/* Hide the "Filtros" title the design system renders internally above
	   "Filtros aplicados (N)" — the drawer already shows its own title/close button */
	.c-applied-filters > div:first-child {
		display: none;
	}
`
